import React, { useState, useEffect } from "react";
import { Download, Share, X, PlusSquare, Copy, Check, Compass } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

// iOS (Safari and any other iOS browser, since they all share WebKit's
// share-sheet) never fires `beforeinstallprompt` — Apple simply doesn't
// expose that API. "Add to Home Screen" there is a manual, user-driven step
// via the Share sheet, so the best we can do is show clear instructions.
//
// iPadOS 13+ Safari reports itself as a Mac ("Macintosh" in the user agent),
// so the old iPhone/iPad regex never matched an iPad and iPad users got no
// instructions at all. A Mac with a touch screen is an iPad.
const isIPadOS = () => navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
const isIOS = () => (/iPad|iPhone|iPod/.test(navigator.userAgent) || isIPadOS()) && !window.MSStream;

/* Where on iOS the page is open decides how — or whether — it can be
   installed:
   - "inapp": inside WhatsApp, Instagram, Facebook, Gmail's viewer and the
     like. Their built-in browsers have NO "Add to Home Screen" at all; the
     page has to be opened in Safari first. This is the usual way a guest or
     staff member arrives, from a shared link, and the old prompt showed
     Safari steps they could not follow.
   - "other": Chrome, Edge or Firefox for iOS — the Share button lives in the
     address bar (iOS 16.4+).
   - "safari": the Share button is in the bottom toolbar on iPhone and the
     top bar on iPad, and "Add to Home Screen" is usually below the first
     row of the share sheet, so people miss it. */
function iosBrowser() {
  const ua = navigator.userAgent;
  if (/FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Twitter|MicroMessenger|Snapchat|LinkedInApp|GSA\/|Pinterest/.test(ua)) return "inapp";
  if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return "other";
  return "safari";
}

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

// No permanent "don't show again" — closing only hides it for this page
// view; it reappears on the next load/refresh until the app is installed,
// per how this was asked to behave.
export default function PwaInstallPrompt() {
  const { hotelName } = useSettings();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed]           = useState(false);
  const [installed, setInstalled]           = useState(isStandalone());

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  if (!deferredPrompt && !isIOS()) return null;

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }

  return (
    // `above-mobile-nav` (index.css) instead of `bottom-4`: at phone widths
    // this card sat directly on top of the bottom tab bar at a higher
    // z-index and swallowed every tap on it — the menu looked fine and
    // simply didn't respond, permanently so on iOS.
    <div className="fixed above-mobile-nav inset-x-4 sm:inset-x-auto sm:right-4 sm:w-96 z-50
      bg-white rounded-2xl border border-gray-200 shadow-xl p-4 flex items-start gap-3">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)]
        flex items-center justify-center shadow-sm flex-shrink-0">
        <Download size={20} className="text-white" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm">Install {hotelName}</p>
        {deferredPrompt ? (
          <>
            <p className="text-xs text-gray-500 mt-0.5">Add it to your home screen for quick, one-tap access.</p>
            <button
              onClick={handleInstall}
              className="mt-2.5 text-xs font-medium px-4 min-h-[44px] rounded-xl
                bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] text-white
                hover:brightness-95 transition-all"
            >
              Install App
            </button>
          </>
        ) : (
          <IosSteps />
        )}
      </div>

      <button
        onClick={() => setDismissed(true)}
        className="-m-2.5 w-11 h-11 flex items-center justify-center rounded-xl text-gray-300
          hover:text-gray-500 hover:bg-gray-50 transition-colors flex-shrink-0"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function IosSteps() {
  const where = iosBrowser();
  const [copied, setCopied] = useState(false);
  async function copyLink() {
    try { await navigator.clipboard.writeText(window.location.origin); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard unavailable — the address is in the steps below */ }
  }

  if (where === "inapp") {
    return (
      <div className="text-xs text-gray-600 mt-0.5 space-y-2">
        <p>This app's browser can't install apps. Open this page in <span className="font-semibold">Safari</span> first:</p>
        <p className="flex flex-wrap items-center gap-1 text-gray-500">
          tap <span className="font-medium text-gray-700">⋯</span> or <Compass size={13} className="inline text-blue-500" />
          → <span className="font-medium text-gray-700">Open in Safari</span>, or copy the link and paste it into Safari.
        </p>
        <button onClick={copyLink}
          className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl text-xs font-semibold border border-gray-200 text-gray-800 hover:bg-gray-50">
          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />} {copied ? "Link copied" : "Copy link"}
        </button>
      </div>
    );
  }

  const shareWhere = where === "other" ? "in the address bar"
    : isIPadOS() || /iPad/.test(navigator.userAgent) ? "at the top right" : "at the bottom of the screen";
  return (
    <ol className="text-xs text-gray-600 mt-1 space-y-1 list-decimal list-inside">
      <li className="flex-wrap">
        Tap Share <Share size={13} className="inline -mt-0.5 text-blue-500" strokeWidth={2} /> {shareWhere}
      </li>
      <li>
        Scroll down, tap <PlusSquare size={13} className="inline -mt-0.5 text-gray-600" strokeWidth={2} />{" "}
        <span className="font-medium text-gray-800">Add to Home Screen</span>
      </li>
      <li>Tap <span className="font-medium text-gray-800">Add</span></li>
      {where === "other" && (
        <li className="list-none text-gray-400 -ml-4">Not there? Open this page in Safari instead.</li>
      )}
    </ol>
  );
}
